// src/components/CheckupQuiz.tsx

import React, { useState } from 'react';

// Definieren des Typs für eine Quizfrage
type QuizQuestion = {
    question: string;
    options: { label: string; value: string }[];
    correctAnswer: string; // Der 'value' der korrekten Option
    explanation?: string; // Optional: Eine Erklärung, die nach dem Aufdecken angezeigt wird
};

// Props für die CheckupQuiz Komponente
type CheckupQuizProps = {
    question1: QuizQuestion;
    question2: QuizQuestion;
    onQuizComplete?: (passed: boolean) => void;
    onBack?: () => void; // <-- Add this line
};

const CheckupQuiz: React.FC<CheckupQuizProps> = ({ onQuizComplete, question1, question2, onBack }) => {
    const [isFlipped1, setIsFlipped1] = useState(false); // State für Karte 1
    const [isFlipped2, setIsFlipped2] = useState(false); // State für Karte 2

    // Gemeinsamer Stil für die Vorder- und Rückseite der Karte
    const commonCardFaceStyle: React.CSSProperties = {
        position: 'absolute',
        width: '100%',
        height: '100%', // Wichtig: Die Höhe soll 100% des Elternteils ausfüllen
        backfaceVisibility: 'hidden',
        borderRadius: '15px',
        padding: '30px', // Etwas weniger Padding, um den Text besser im Rahmen zu halten
        boxShadow: '0 6px 12px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        textAlign: 'left',
        transition: 'transform 0.6s',
        overflow: 'auto', // Wichtig: Inhalt wird scrollbar, wenn er überläuft
        boxSizing: 'border-box', // Padding und Border in die Breite/Höhe einbeziehen
    };

    const cardContainerStyle: React.CSSProperties = {
        width: 'calc(50% - 20px)',
        height: '350px', // Feste Höhe für die Karten, um Konsistenz zu gewährleisten
        perspective: '1000px',
        position: 'relative',
        marginBottom: '20px', // Abstand zwischen den Karten und dem Button
    };

    const cardInnerStyle: React.CSSProperties = {
        position: 'relative',
        width: '100%',
        height: '100%',
        textAlign: 'center',
        transition: 'transform 0.6s',
        transformStyle: 'preserve-3d',
    };

    const frontCardStyle: React.CSSProperties = {
        ...commonCardFaceStyle,
        backgroundColor: '#f8f8f8',
        zIndex: 2,
        cursor: 'pointer',
    };

    const backCardStyle: React.CSSProperties = {
        ...commonCardFaceStyle,
        backgroundColor: '#e6f7ff',
        transform: 'rotateY(180deg)',
        zIndex: 1,
        cursor: 'default',
    };

    const questionTextStyle: React.CSSProperties = {
        fontSize: '1.4em', // Etwas kleinere Schrift, um Überlauf zu vermeiden
        fontWeight: 'bold', // Frage etwas stärker hervorheben
        color: '#333',
        marginBottom: '15px', // Etwas weniger Abstand
    };

    const answerOptionStyle: React.CSSProperties = {
        marginBottom: '8px', // Etwas weniger Abstand zwischen Optionen
        fontSize: '1.1em', // Etwas kleinere Schrift
        color: '#333',
    };

    const correctAnswerStyle: React.CSSProperties = {
        ...answerOptionStyle,
        fontWeight: 'bold',
        color: '#4CAF50',
    };

    const explanationStyle: React.CSSProperties = {
        fontSize: '1em', // Etwas kleinere Schrift
        color: '#666',
        marginTop: '10px', // Etwas weniger Abstand zur Antwort
        fontStyle: 'italic',
    };

    const clickToFlipStyle: React.CSSProperties = {
        position: 'absolute',
        bottom: '20px', // Angepasst an das neue Padding
        left: '30px', // Angepasst an das neue Padding
        fontSize: '0.9em', // Kleinere Schrift, unauffälliger
        color: '#888',
    };

    // Helferfunktion, um die richtige Option zu finden
    const getCorrectOptionLabel = (question: QuizQuestion) => {
        const correctOption = question.options.find(opt => opt.value === question.correctAnswer);
        return correctOption ? correctOption.label : 'Antwort nicht gefunden';
    };

    // Überprüfen, ob beide Karten umgedreht sind
    const allQuestionsAnswered = isFlipped1 && isFlipped2;

    const handleQuizDoneClick = () => {
        if (onQuizComplete) {
            onQuizComplete(true); // Quiz ist abgeschlossen
        }
    };

    return (
        <div style={{
            marginTop: '50px',
            marginBottom: '50px',
        }}>
            <h2 style={{ fontSize: '2em', fontWeight: '800', color: '#333', marginBottom: '35px', textAlign: 'center' }}>
                Testet euer Wissen!
            </h2>

            <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '40px',
                flexWrap: 'wrap',
                width: '100%', // Stellt sicher, dass der Flex-Container die volle Breite nutzt
                boxSizing: 'border-box',
            }}>
                {/* Karte 1 */}
                <div style={cardContainerStyle}>
                    <div style={{ ...cardInnerStyle, transform: isFlipped1 ? 'rotateY(180deg)' : 'rotateY(0deg)' }}>
                        {/* Vorderseite der Karte 1 (Frage) */}
                        <div style={frontCardStyle} onClick={!isFlipped1 ? () => setIsFlipped1(true) : undefined}>
                            <h4 style={questionTextStyle}>
                                {question1.question}
                            </h4>
                            {/* Inhalt der Frage */}
                            <div style={{flexGrow: 1}}> {/* Dies lässt den Optionenbereich wachsen und scrollbar werden */}
                                {question1.options.map((option, index) => (
                                    <p key={index} style={answerOptionStyle}>
                                        {option.label}
                                    </p>
                                ))}
                            </div>
                            {!isFlipped1 && (
                                <div style={{ marginTop: '20px', fontSize: '0.9em', color: '#888' }}>
                                    Klicke auf die Karte, um die Antwort zu sehen
                                </div>
                            )}
                        </div>

                        {/* Rückseite der Karte 1 (Antwort) */}
                        <div style={backCardStyle}>
                            <h4 style={questionTextStyle}>
                                Die richtige Antwort:
                            </h4>
                            {/* Inhalt der Antwort */}
                            <div style={{flexGrow: 1}}>
                                <p style={correctAnswerStyle}>
                                    {getCorrectOptionLabel(question1)} (Richtig!)
                                </p>
                                {question1.explanation && (
                                    <p style={explanationStyle}>{question1.explanation}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Karte 2 */}
                <div style={cardContainerStyle}>
                    <div style={{ ...cardInnerStyle, transform: isFlipped2 ? 'rotateY(180deg)' : 'rotateY(0deg)' }}>
                        {/* Vorderseite der Karte 2 (Frage) */}
                        <div style={frontCardStyle} onClick={!isFlipped2 ? () => setIsFlipped2(true) : undefined}>
                            <h4 style={questionTextStyle}>
                                {question2.question}
                            </h4>
                            {/* Inhalt der Frage */}
                            <div style={{flexGrow: 1}}>
                                {question2.options.map((option, index) => (
                                    <p key={index} style={answerOptionStyle}>
                                        {option.label}
                                    </p>
                                ))}
                            </div>
                            {!isFlipped2 && (
                                <div style={{ marginTop: '20px', fontSize: '0.9em', color: '#888' }}>
                                    Klicke auf die Karte, um die Antwort zu sehen
                                </div>

                            )}
                        </div>

                        {/* Rückseite der Karte 2 (Antwort) */}
                        <div style={backCardStyle}>
                            <h4 style={questionTextStyle}>
                                Die richtige Antwort:
                            </h4>
                            {/* Inhalt der Antwort */}
                            <div style={{flexGrow: 1}}>
                                <p style={correctAnswerStyle}>
                                    {getCorrectOptionLabel(question2)} (Richtig!)
                                </p>
                                {question2.explanation && (
                                    <p style={explanationStyle}>{question2.explanation}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Button row: "Zurück" always left, "Quiz beenden" right if both cards flipped */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '40px',
                width: '100%',
                maxWidth: '700px',
                marginLeft: 'auto',
                marginRight: 'auto',
                gap: '10px',
            }}>
                <button
                    onClick={onBack}
                    style={{
                        backgroundColor: '#f0f0f0',
                        color: '#555',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '12px 25px',
                        cursor: 'pointer',
                        fontSize: '1.1em',
                        fontWeight: 600,
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                        transition: 'background-color 0.2s ease',
                        minWidth: '120px',
                    }}
                    onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                    onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                >
                    Zurück
                </button>
                {allQuestionsAnswered ? (
                    <button onClick={handleQuizDoneClick} style={{
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '15px 30px',
                        cursor: 'pointer',
                        fontSize: '1.2em',
                        boxShadow: '0 3px 6px rgba(0, 0, 0, 0.15)',
                        transition: 'background-color 0.2s ease',
                        minWidth: '120px',
                    }} onMouseOver={e => e.currentTarget.style.backgroundColor = '#45a049'} onMouseOut={e => e.currentTarget.style.backgroundColor = '#4CAF50'}>
                        Weiter
                    </button>
                ) : (
                    <div style={{ minWidth: '120px' }} /> // Keeps spacing
                )}
            </div>
        </div>
    );
};

export default CheckupQuiz;